export interface PatchOperation {
  type: "replace" | "insert_before" | "insert_after";
  target_text: string;
  new_text: string;
}

export interface SkillPatch {
  operations: PatchOperation[];
  success?: boolean;
  failedIndex?: number;
}

export function parseSkillPatch(input: unknown): SkillPatch | undefined {
  const raw = record(input)?.["operations"];
  if (!Array.isArray(raw) || raw.length === 0 || raw.length > 20) return;
  const operations: PatchOperation[] = [];
  let total = 0;
  for (const value of raw) {
    const op = record(value);
    if (!op || !["replace", "insert_before", "insert_after"].includes(String(op["type"])) ||
        typeof op["target_text"] !== "string" || !op["target_text"] || op["target_text"].length > 8000 ||
        typeof op["new_text"] !== "string" || op["new_text"].length > 20000) return;
    total += op["new_text"].length;
    if (total > 60000) return;
    operations.push({ type: op["type"] as PatchOperation["type"], target_text: op["target_text"], new_text: op["new_text"] });
  }
  return { operations };
}

export function patchOutcome(output: unknown): Pick<SkillPatch, "success" | "failedIndex"> {
  let value = output;
  for (let depth = 0; depth < 8; depth++) {
    if (typeof value === "string") {
      try { value = JSON.parse(value); } catch { return {}; }
      continue;
    }
    const obj = record(value);
    if (!obj) return {};
    if (obj["lc"] === 1) { value = obj["kwargs"]; continue; }
    if (typeof obj["content"] === "string") { value = obj["content"]; continue; }
    if (Array.isArray(obj["content"])) {
      value = record(obj["content"].find((part) => record(part)?.["type"] === "text"))?.["text"];
      continue;
    }
    if (typeof obj["success"] !== "boolean") return {};
    const index = obj["operation_index"];
    return { success: obj["success"], ...(obj["success"] === false && Number.isInteger(index) && Number(index) >= 0 ? { failedIndex: Number(index) } : {}) };
  }
  return {};
}

export interface DiffLine {
  kind: "context" | "remove" | "add";
  text: string;
  highlight?: [number, number];
}

export function operationDiff(op: PatchOperation): DiffLine[] {
  const before = op.target_text.replace(/\r\n?/g, "\n");
  const replacement = op.new_text.replace(/\r\n?/g, "\n");
  const after = op.type === "insert_before" ? replacement + before : op.type === "insert_after" ? before + replacement : replacement;
  let prefix = 0;
  while (prefix < before.length && prefix < after.length && before[prefix] === after[prefix]) prefix++;
  if (prefix > 0 && /[\uD800-\uDBFF]/.test(before[prefix - 1]!)) prefix--;
  let suffix = 0;
  while (suffix < before.length - prefix && suffix < after.length - prefix && before[before.length - 1 - suffix] === after[after.length - 1 - suffix]) suffix++;
  if (suffix > 0 && /[\uDC00-\uDFFF]/.test(before[before.length - suffix]!)) suffix--;
  const lines = (text: string, kind: "remove" | "add"): DiffLine[] => {
    if (!text) return [];
    const parts = text.split("\n");
    if (parts.at(-1) === "") parts.pop();
    let offset = 0;
    return parts.map((line) => {
      const start = Math.max(0, prefix - offset);
      const end = Math.min(line.length, text.length - suffix - offset);
      offset += line.length + 1;
      return { kind, text: line, ...(end > start ? { highlight: [start, end] as [number, number] } : {}) };
    });
  };
  const old = lines(before, "remove"), next = lines(after, "add");
  // Bound the LCS allocation for large multi-line operations.
  if (old.length * next.length > 20000) return [...old, ...next];
  const dp = Array.from({ length: old.length + 1 }, () => new Uint32Array(next.length + 1));
  for (let i = old.length - 1; i >= 0; i--) {
    for (let j = next.length - 1; j >= 0; j--) {
      dp[i]![j] = old[i]!.text === next[j]!.text ? dp[i + 1]![j + 1]! + 1 : Math.max(dp[i + 1]![j]!, dp[i]![j + 1]!);
    }
  }
  const result: DiffLine[] = [];
  let i = 0, j = 0;
  while (i < old.length || j < next.length) {
    if (i < old.length && j < next.length && old[i]!.text === next[j]!.text) {
      result.push({ kind: "context", text: old[i++]!.text }); j++;
    } else if (i < old.length && (j === next.length || dp[i + 1]![j]! >= dp[i]![j + 1]!)) {
      result.push(old[i++]!);
    } else result.push(next[j++]!);
  }
  return result;
}

export interface ParameterChange {
  name: string;
  label: string;
  kind: "added" | "removed" | "modified";
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  const obj = record(value);
  return obj ? `{${Object.keys(obj).sort().map((key) => `${JSON.stringify(key)}:${canonical(obj[key])}`).join(",")}}` : JSON.stringify(value) ?? "";
}

export function parameterChanges(baseline: unknown, proposed: unknown): ParameterChange[] {
  if (!Array.isArray(proposed)) return [];
  const parse = (items: unknown[]): Record<string, unknown>[] => items.flatMap((item) => {
    const obj = record(item);
    return obj && typeof obj["name"] === "string" ? [obj] : [];
  });
  const old = parse(Array.isArray(baseline) ? baseline : []);
  const next = parse(proposed).map((item) => {
    const previous = old.find((entry) => entry["name"] === item["name"]);
    if (!previous) return item;
    const merged = { ...previous, ...item };
    if (typeof item["displayName"] !== "string" || !item["displayName"].trim()) {
      if (previous["displayName"] !== undefined) merged["displayName"] = previous["displayName"];
      else delete merged["displayName"];
    }
    return merged;
  });
  const withPosition = (item: Record<string, unknown>, index: number): Record<string, unknown> => ({ ...item, position: index + 1 });
  const changes: ParameterChange[] = [];
  for (const name of new Set([...next, ...old].map((item) => String(item["name"])))) {
    const i = old.findIndex((item) => item["name"] === name), j = next.findIndex((item) => item["name"] === name);
    const before = i >= 0 ? withPosition(old[i]!, i) : undefined, after = j >= 0 ? withPosition(next[j]!, j) : undefined;
    if (canonical(before) === canonical(after)) continue;
    const label = after?.["displayName"] ?? before?.["displayName"];
    changes.push({ name, label: typeof label === "string" ? label : name, kind: !before ? "added" : !after ? "removed" : "modified", ...(before ? { before } : {}), ...(after ? { after } : {}) });
  }
  return changes;
}
